// private-route.tsx - Fixed version
import React, { ReactElement, useCallback, useContext } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import { Flex, Spin } from "antd";

import { useAuth } from "../../context/AuthContext";
import routeNames from "../../constants/route-names";
import { UserRoleValue } from "../../constants/field-values";
import { useGetUserByIdQuery } from "../../graphql/queries/get-user-by-id.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { hasRoutePermission } from "../../routes/route-permissions";
import { buildLoginPath } from "utils/auth-redirect";
import NetworkStatusAlert from "components/network-status-alert/network-status-alert";
import { useNetworkStatus } from "context/NetworkStatusContext";

// A logged-in screen sits inside two of these: the layout's authentication gate and, on
// the routes that have one, its own role gate. Only the outermost renders the offline
// banner — this is how the inner one knows it is already on screen.
const NetworkAlertContext = React.createContext(false);

interface PrivateRouteProps {
	children: React.ReactNode;
	requiredRoute?: string;
	requireHighnoteAdmin?: boolean;
	requireWorkspaceAdmin?: boolean;
	noPermissionsRequired?: boolean;
	allowAnonymous?: boolean;
	redirectPath?: string;
	showNetworkStatusAlert?: boolean;
}

const PrivateRoute = (props: PrivateRouteProps): ReactElement => {
	const {
		children,
		requiredRoute,
		requireHighnoteAdmin,
		redirectPath,
		noPermissionsRequired,
		allowAnonymous,
		showNetworkStatusAlert = true
	} = props;

	const location = useLocation();
	const { loggedUser } = useAuth();
	const { updateLoggedUser, loggedUserValue } = useLoggedUser();
	const { isOffline } = useNetworkStatus();
	const alertShownByAncestor = useContext(NetworkAlertContext);

	const redirectRoute = loggedUserValue ? redirectPath || routeNames.HOME : routeNames.LOGIN;

	const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
		console.log("PrivateRoute Error Get Users: ", error);
		// NetworkStatusContext automatically handles this error through Apollo error link
	}, []);

	const handleOnCompletedGetUsers = useCallback(
		data => {
			// NetworkStatusContext automatically tracks successful operations
			const { users_by_pk } = data;
			if (users_by_pk[0]) {
				// In this case, we're getting data from the server
				// The updateLoggedUser function will preserve selected properties
				updateLoggedUser(users_by_pk[0]);
			}
		},
		[updateLoggedUser]
	);

	const { data, error, loading, refetch } = useGetUserByIdQuery({
		skip: !loggedUser?.uid,
		variables: {
			id: loggedUser?.uid,
			is_not_deleted: true
		},
		errorPolicy: "all",
		fetchPolicy: "network-only",
		nextFetchPolicy: "cache-first", // Use cache after successful fetch
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorsGetUsers,
		onCompleted: handleOnCompletedGetUsers
	});

	// First check if user is authenticated with Firebase
	if (!loggedUser) {
		if (allowAnonymous) {
			return <>{children}</>;
		}
		// Save the attempted URL for redirecting after login
		return <Navigate to={buildLoginPath(location)} replace />;
	}

	// Verify if the Firebase user matches our stored user
	if (loggedUserValue && loggedUserValue.id && loggedUserValue.id !== loggedUser.uid) {
		console.error("Firebase user mismatch!");
		return <Navigate to={routeNames.LOGIN} replace />;
	}

	// Check if user needs email verification (Firebase user exists but email not verified)
	if (loggedUser && !loggedUser.emailVerified) {
		// Only allow access to email verification pending page
		if (location.pathname !== routeNames.EMAIL_VERIFICATION_PENDING) {
			return (
				<Navigate
					to={`${routeNames.EMAIL_VERIFICATION_PENDING}?email=${encodeURIComponent(loggedUser.email || "")}`}
					replace
				/>
			);
		}
	}

	// Determine user data source (fresh from server or cached)
	const userData = data?.users_by_pk[0] || loggedUserValue;
	const usingCachedData = !data?.users_by_pk[0] && !!loggedUserValue && !loading;

	// Handle errors - only redirect to login for non-network errors when we have no cached data
	if (error && !userData) {
		const isNetworkError =
			!!error.networkError ||
			error.message?.includes("timeout") ||
			error.message?.includes("fetch") ||
			error.message?.includes("Network");

		if (!isNetworkError) {
			console.error("Error fetching user data: ", error);
			return <Navigate to={routeNames.LOGIN} />;
		}
	}

	// Rendered by both paths that grant access, so the banner rule lives in one place.
	const granted = (
		<NetworkAlertContext.Provider value={alertShownByAncestor || showNetworkStatusAlert}>
			{showNetworkStatusAlert && !alertShownByAncestor && (
				<NetworkStatusAlert visible={isOffline || usingCachedData} onRetry={refetch} loading={loading} />
			)}
			{children}
		</NetworkAlertContext.Provider>
	);

	// If no permissions are required we only check basic authentication, allow access
	if (noPermissionsRequired) {
		return granted;
	} else {
		// Show loading state while fetching user data (only if we have no cached data)
		if (loading && !userData) {
			return (
				<Flex
					style={{
						height: "80%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center"
					}}
				>
					<Spin />
				</Flex>
			);
		}

		// Use either fresh data or cached data
		if (userData) {
			const { role } = userData || {};
			const userRole = role as UserRoleValue;

			// Verification of permits by route
			if (requiredRoute && hasRoutePermission(requiredRoute, userRole)) {
				return granted;
			} else {
				console.log(`User with role ${userRole} does not have permission for route ${requiredRoute}`);
				return <Navigate to={redirectRoute} replace />;
			}
		} else {
			// No user data and no cached data - redirect to login
			return <Navigate to={routeNames.LOGIN} />;
		}
	}
};

export default PrivateRoute;
